class ProductForm {
  constructor() {
    this.isEditMode = false;
    this.productId = null;
    this.varietyCounter = 0;
    this.init();
  }

  init() {
    this.checkEditMode();
    this.bindEvents();
    this.setupImagePreview();
    this.loadAllProductTitles().then(() => {
      this.initializeTitleInputState();
    });
  }

  checkEditMode() {
    const urlParams = new URLSearchParams(window.location.search);
    this.isEditMode = urlParams.has("id");
    this.productId = urlParams.get("id");

    if (this.isEditMode) {
      this.prefillForm();
    }
  }

  prefillForm() {
    document.addEventListener("DOMContentLoaded", () => {
      const urlParams = new URLSearchParams(window.location.search);
      const title = urlParams.get("title") || "";
      const quantity = urlParams.get("quantity") || "";

      // Update form title
      const formTitle = document.querySelector("h2");
      if (formTitle) {
        formTitle.textContent = "Edit Product";
      }

      // Pre-fill form fields
      if (title) {
        document.getElementById("product-title-input").value = title;
        document.getElementById("product-title-select").value = title;
      }
      if (quantity) {
        document.getElementById("quantity").value = quantity;
      }

      // Update submit button text
      const submitBtn = document.querySelector('button[type="submit"]');
      if (submitBtn) {
        submitBtn.textContent = "Update Product";
      }

      // Initialize title input state after pre-filling
      this.initializeTitleInputState();
    });
  }

  bindEvents() {
    document
      .getElementById("grower-product-form")
      .addEventListener("submit", (e) => {
        this.handleFormSubmit(e);
      });

    // Refresh titles button
    document
      .getElementById("refresh-titles-btn")
      .addEventListener("click", () => {
        this.refreshProductTitles();
      });

    // Title select change event to disable/enable title input
    document
      .getElementById("product-title-select")
      .addEventListener("change", (e) => {
        this.handleTitleSelectChange(e);
      });

    // Title input event to clear select when typing
    document
      .getElementById("product-title-input")
      .addEventListener("input", (e) => {
        this.handleTitleInputChange(e);
      });

    // Clear validation errors on input
    this.setupValidationClearListeners();
  }

  async refreshProductTitles() {
    const refreshBtn = document.getElementById("refresh-titles-btn");

    // Disable button during refresh
    refreshBtn.disabled = true;
    refreshBtn.style.transform = "rotate(360deg)";

    try {
      await this.loadAllProductTitles();
    } catch (error) {
      console.error("Error refreshing titles:", error);
    } finally {
      // Re-enable button
      refreshBtn.disabled = false;
      refreshBtn.style.transform = "";
    }
  }

  async loadAllProductTitles() {
    try {
      const select = document.getElementById("product-title-select");
      const refreshBtn = document.getElementById("refresh-titles-btn");

      // Show loading state
      select.innerHTML = '<option value="">Loading titles...</option>';
      if (refreshBtn) {
        refreshBtn.disabled = true;
      }

      // Fetch from both APIs
      const promises = [];
      // Backend products
      promises.push(this.getAllBackendProducts());
      // Shopify products
      try {
        promises.push(this.getAllShopifyProducts());
      } catch (error) {
        console.warn("Could not fetch Shopify products:", error);
      }

      const results = await Promise.allSettled(promises);

      // Collect all titles with source information
      const allTitles = new Map(); // title -> { sources: Set, title: string }

      // Process backend products
      if (results[0] && results[0].status === "fulfilled") {
        const backendProducts = results[0].value;
        if (Array.isArray(backendProducts)) {
          backendProducts.forEach((product) => {
            if (product && product.title) {
              const title = product.title.trim();
              if (title && !allTitles.has(title)) {
                allTitles.set(title, { sources: new Set(), title });
              }
              if (title) {
                allTitles.get(title).sources.add("Backend");
              }
            }
          });
        }
      }

      // Process Shopify products
      if (results[1] && results[1].status === "fulfilled") {
        const shopifyProducts = results[1].value;
        if (Array.isArray(shopifyProducts)) {
          shopifyProducts.forEach((product) => {
            if (product && product.title) {
              const title = product.title.trim();
              if (title && !allTitles.has(title)) {
                allTitles.set(title, { sources: new Set(), title });
              }
              if (title) {
                allTitles.get(title).sources.add("Shopify");
              }
            }
          });
        }
      }

      // Populate dropdown
      select.innerHTML =
        '<option value="">Select existing title or add new</option>';

      // Sort titles alphabetically and add to dropdown
      const sortedTitles = Array.from(allTitles.values()).sort((a, b) =>
        a.title.localeCompare(b.title)
      );
      sortedTitles.forEach(({ title, sources }) => {
        const option = document.createElement("option");
        option.value = title;
        option.textContent = title;

        select.appendChild(option);
      });

      // Show success message to user
      if (sortedTitles.length > 0) {
        this.createToast(
          "success",
          `Loaded ${sortedTitles.length} existing product titles`
        );
      }
    } catch (error) {
      console.error("Error loading product titles:", error);

      // Fallback to basic dropdown
      const select = document.getElementById("product-title-select");
      select.innerHTML =
        '<option value="">Select existing title or add new</option>';

      // Show error toast
      this.createToast(
        "error",
        "Could not load existing product titles. You can still enter a new title."
      );
    } finally {
      // Re-enable refresh button
      const refreshBtn = document.getElementById("refresh-titles-btn");
      if (refreshBtn) {
        refreshBtn.disabled = false;
      }
    }
  }

  setupImagePreview() {
    document
      .getElementById("product-images")
      .addEventListener("change", (e) => {
        this.handleImagePreview(e);
      });
  }

  handleImagePreview(e) {
    const previewContainer = document.getElementById("image-preview");
    previewContainer.innerHTML = "";

    const files = Array.from(e.target.files);

    // Add image count display
    const countDisplay = document.createElement("div");
    countDisplay.className = "image-count-display";

    if (files.length < 2) {
      countDisplay.innerHTML = `
        <span class="validation-error">${files.length} image(s) uploaded. At least 2 images are required.</span>
      `;
    } else {
      countDisplay.innerHTML = `
        <span class="validation-success">${files.length} image(s) uploaded. ✓</span>
      `;
    }

    previewContainer.appendChild(countDisplay);

    files.forEach((file, index) => {
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const previewItem = document.createElement("div");
          previewItem.className = "image-preview-item";
          previewItem.innerHTML = `
              <img src="${e.target.result}" alt="Preview">
              <button type="button" class="image-preview-remove" onclick="productForm.removeImagePreview(${index})">×</button>
            `;
          previewContainer.appendChild(previewItem);
        };
        reader.readAsDataURL(file);
      }
    });
  }

  removeImagePreview(index) {
    const input = document.getElementById("product-images");
    const dt = new DataTransfer();
    const files = Array.from(input.files);

    files.forEach((file, i) => {
      if (i !== index) {
        dt.items.add(file);
      }
    });

    input.files = dt.files;

    // Re-trigger preview to update count display
    const event = new Event("change");
    input.dispatchEvent(event);
  }

  async uploadImages(files, productId) {
    const uploaded = [];

    for (let file of files) {
      const formData = new FormData();
      formData.append("image", file);

      try {
        const response = await fetch(
          `https://growers-bk.vercel.app/api/images/upload/${productId}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${
                JSON.parse(sessionStorage.getItem("growerToken") || "{}").token
              }`,
            },
            body: formData,
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to upload image");
        }

        const data = await response.json();
        uploaded.push(data.image);
      } catch (error) {
        console.error("Image upload error:", error);
        throw error;
      }
    }

    return uploaded;
  }

  createToast(type, message) {
    const toastContainer = document.getElementById("toastContainer");
    const toast = document.createElement("div");
    toast.className = `toast ${type}`;
    const icon = type === "error" ? "✕" : "✓";
    toast.innerHTML = `
        <div class="toast-content">
          <div class="toast-icon">${icon}</div>
          <p class="toast-message">${message}</p>
        </div>
      `;
    toastContainer.appendChild(toast);
    // Trigger animation
    requestAnimationFrame(() => {
      toast.classList.add("show");
    });
    // Auto remove after 5 seconds
    setTimeout(() => {
      toast.classList.remove("show");
      setTimeout(() => toast.remove(), 300);
    }, 5000);
  }

  showSpinner(message = "Saving product...") {
    const spinnerOverlay = document.getElementById("spinnerOverlay");
    const spinnerText = document.getElementById("spinnerText");
    spinnerText.textContent = message;
    spinnerOverlay.style.display = "flex";
  }

  hideSpinner() {
    const spinnerOverlay = document.getElementById("spinnerOverlay");
    spinnerOverlay.style.display = "none";
  }

  generateSku(growerId, dahliaVariety) {
    const varietySku = dahliaVariety.replace(/\s+/g, "-").toUpperCase();
    const timestamp = Date.now().toString().slice(-6); // Last 6 digits of timestamp
    const randomSuffix = Math.random()
      .toString(36)
      .substring(2, 6)
      .toUpperCase(); // 4 random characters
    return `${growerId}-${varietySku}-${timestamp}-${randomSuffix}`;
  }

  getGrowerToken() {
    const sessionToken = sessionStorage.getItem("growerToken");
    const growerToken = JSON.parse(sessionToken || "{}");
    if (!growerToken.token) {
      throw new Error("Please log in to add products");
    }
    return growerToken;
  }

  async createOrUpdateProduct(productPayload, method, url, token) {
    const response = await fetch(url, {
      method: method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(productPayload),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message ||
          `Failed to ${this.isEditMode ? "update" : "create"} product`
      );
    }

    return await response.json();
  }

  async getAllShopifyProducts() {
    const response = await fetch(
      "https://growers-bk.vercel.app/api/shopify/products",
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch Shopify products: ${response.status}`);
    }

    const data = await response.json();
    // Shopify API returns data.data
    return data.data.products || [];
  }

  async getAllBackendProducts() {
    try {
      const growerToken = this.getGrowerToken();
      const growerId = growerToken.grower.id;
      const response = await fetch(
        `https://growers-bk.vercel.app/api/products?growerId=${growerId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${growerToken.token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch backend products: ${response.status}`);
      }

      const data = await response.json();
      // Backend API returns data.products
      return data.products || [];
    } catch (error) {
      console.warn(
        "Could not fetch backend products (user may not be logged in):",
        error
      );
      return [];
    }
  }

  async uploadImagesToGoogleAppsScript(files, shopifyProductId) {
    if (!files || files.length === 0) {
      console.log("No files provided, returning early");
      return [];
    }

    try {
      // Step 1: Prepare metadata for stagedUploadsCreate
      const imageMetadata = files.map((file) => ({
        filename: file.name,
        mimeType: file.type,
        resource: "FILE",
        fileSize: file.size.toString(),
        httpMethod: "POST",
      }));

      // Step 2: Call /upload-images to get staged upload URLs
      const uploadRes = await fetch(
        `https://growers-bk.vercel.app/api/shopify/upload-images`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ files: imageMetadata }),
        }
      );

      if (!uploadRes.ok) {
        throw new Error(`Upload images API failed: ${uploadRes.status}`);
      }

      const uploadData = await uploadRes.json();
      const stagedTargets =
        uploadData?.data?.stagedUploadsCreate?.stagedTargets;

      if (!stagedTargets || stagedTargets.length === 0) {
        throw new Error("No staged targets returned from upload API");
      }

      // Step 3: Upload each file to the corresponding staged URL
      await Promise.all(
        stagedTargets.map(async (target, index) => {
          const formData = new FormData();

          // Add all required parameters
          target.parameters.forEach((param) => {
            formData.append(param.name, param.value);
          });

          // Add the actual file
          formData.append("file", files[index]);

          // Upload to Shopify S3
          const s3Res = await fetch(target.url, {
            method: "POST",
            body: formData,
          });

          if (!s3Res.ok) {
            throw new Error(
              `Failed to upload file to Shopify S3: ${s3Res.status}`
            );
          }
        })
      );

      // Step 4: Register uploaded files with Shopify using fileCreate
      const fileInputs = stagedTargets.map((item, index) => ({
        alt: "product image-" + index,
        originalSource: item.resourceUrl,
        contentType: "IMAGE",
      }));

      const createRes = await fetch(
        `https://growers-bk.vercel.app/api/shopify/create-url`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ files: fileInputs }),
        }
      );

      if (!createRes.ok) {
        throw new Error(`Create URL API failed: ${createRes.status}`);
      }

      const createData = await createRes.json();
      const fileIds = createData?.data?.fileCreate?.files?.map((f) => f.id);

      if (!fileIds || fileIds.length === 0) {
        throw new Error("No file IDs returned from create URL API");
      }

      // Optional: Wait for Shopify to generate previews
      await new Promise((resolve) => setTimeout(resolve, 3000));

      // Step 5: Fetch preview images using file IDs
      const previewRes = await fetch(
        `https://growers-bk.vercel.app/api/shopify/preview-image`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ ids: fileIds }),
        }
      );
      return previewRes;
    } catch (error) {
      console.error("Google Apps Script upload error:", error);
      throw new Error(`Failed to upload images to Shopify: ${error.message}`);
    }
  }

  validateForm() {
    // Clear any previous validation errors
    this.clearValidationErrors();

    let isValid = true;
    const errors = [];

    // Validate product title
    const titleInput = document.getElementById("product-title-input");
    const titleSelect = document.getElementById("product-title-select");
    const titleInputValue = titleInput.value.trim();
    const titleSelectValue = titleSelect.value;
    const title = titleInputValue || titleSelectValue;

    if (!title) {
      this.showFieldError("product-title-input", "Product title is required");
      errors.push("Product title is required");
      isValid = false;
    }

    // Validate total quantity
    const quantityInput = document.getElementById("quantity");
    const quantity = parseInt(quantityInput.value, 10);

    if (!quantityInput.value || isNaN(quantity) || quantity < 0) {
      this.showFieldError(
        "quantity",
        "Please enter a valid quantity (0 or greater)"
      );
      errors.push("Valid quantity is required");
      isValid = false;
    }

    // Validate product images
    const fileInput = document.getElementById("product-images");
    const files = Array.from(fileInput.files);

    if (files.length < 2) {
      this.showFieldError(
        "product-images",
        "Please upload at least 2 product images"
      );
      errors.push("At least 2 product images are required");
      isValid = false;
    } else {
      // Validate file types and sizes
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
      const maxSize = 5 * 1024 * 1024; // 5MB

      for (let file of files) {
        if (!validTypes.includes(file.type)) {
          this.showFieldError(
            "product-images",
            "Only JPEG, PNG, and WebP images are allowed"
          );
          errors.push("Invalid image format");
          isValid = false;
          break;
        }
        if (file.size > maxSize) {
          this.showFieldError(
            "product-images",
            "Each image must be less than 5MB"
          );
          errors.push("Image size too large");
          isValid = false;
          break;
        }
      }
    }

    if (!isValid) {
      this.createToast(
        "error",
        `Please fix the following errors:\n${errors.join("\n")}`
      );
    }

    return isValid;
  }

  showFieldError(fieldElement, message) {
    if (typeof fieldElement === "string") {
      fieldElement = document.getElementById(fieldElement);
    }

    if (fieldElement) {
      fieldElement.classList.add("error");

      // Remove existing error message
      const existingError =
        fieldElement.parentNode.querySelector(".error-message");
      if (existingError) {
        existingError.remove();
      }

      // Add new error message
      const errorDiv = document.createElement("div");
      errorDiv.className = "error-message";
      errorDiv.textContent = message;
      fieldElement.parentNode.appendChild(errorDiv);
    }
  }

  clearFieldError(fieldElement) {
    if (typeof fieldElement === "string") {
      fieldElement = document.getElementById(fieldElement);
    }

    if (fieldElement) {
      fieldElement.classList.remove("error");

      // Remove existing error message
      const existingError =
        fieldElement.parentNode.querySelector(".error-message");
      if (existingError) {
        existingError.remove();
      }
    }
  }

  clearValidationErrors() {
    // Remove error classes
    const errorFields = document.querySelectorAll(".error");
    errorFields.forEach((field) => {
      field.classList.remove("error");
    });

    // Remove error messages
    const errorMessages = document.querySelectorAll(".error-message");
    errorMessages.forEach((msg) => {
      msg.remove();
    });

    // Clear image count display
    const imageCountDisplay = document.querySelector(".image-count-display");
    if (imageCountDisplay) {
      imageCountDisplay.remove();
    }
  }

  setupValidationClearListeners() {
    // Clear validation errors when user starts typing/selecting
    const fieldsToWatch = ["quantity", "product-images"];

    fieldsToWatch.forEach((fieldId) => {
      const field = document.getElementById(fieldId);
      if (field) {
        field.addEventListener("input", () => {
          this.clearFieldError(field);
        });
        field.addEventListener("change", () => {
          this.clearFieldError(field);
        });
      }
    });

    // Clear variety error when user adds a variety
    const addVarietyBtn = document.getElementById("add-variety-btn");
    if (addVarietyBtn) {
      addVarietyBtn.addEventListener("click", () => {
        this.clearFieldError(addVarietyBtn);
      });
    }
  }

  handleTitleSelectChange(e) {
    const selectedTitle = e.target.value;
    const titleInput = document.getElementById("product-title-input");
    const statusText = document.getElementById("title-status-text");

    if (selectedTitle && selectedTitle.trim() !== "") {
      // Existing title selected - disable input and set value
      titleInput.disabled = true;
      titleInput.value = selectedTitle;
      titleInput.classList.add("disabled");
      this.clearFieldError("product-title-input");

      // Update status text
      if (statusText) {
        statusText.textContent = `Using existing title: "${selectedTitle}"`;
        statusText.className = "form-text validation-success";
      }
    } else {
      // No title selected - enable input for new title
      titleInput.disabled = false;
      titleInput.value = "";
      titleInput.classList.remove("disabled");

      // Update status text
      if (statusText) {
        statusText.textContent = "Select an existing title or type a new one";
        statusText.className = "form-text";
      }
    }
  }

  initializeTitleInputState() {
    const titleSelect = document.getElementById("product-title-select");
    const titleInput = document.getElementById("product-title-input");
    const statusText = document.getElementById("title-status-text");

    if (titleSelect && titleInput) {
      const selectedValue = titleSelect.value;
      if (selectedValue && selectedValue.trim() !== "") {
        // If there's a pre-selected value (e.g., edit mode), disable input
        titleInput.disabled = true;
        titleInput.value = selectedValue;
        titleInput.classList.add("disabled");

        // Update status text
        if (statusText) {
          statusText.textContent = `Using existing title: "${selectedValue}"`;
          statusText.className = "form-text validation-success";
        }
      } else {
        // No selection, enable input for new title
        titleInput.disabled = false;
        titleInput.classList.remove("disabled");

        // Update status text
        if (statusText) {
          statusText.textContent = "Select an existing title or type a new one";
          statusText.className = "form-text";
        }
      }
    }
  }

  handleTitleInputChange(e) {
    const titleInput = e.target;
    const titleSelect = document.getElementById("product-title-select");
    const statusText = document.getElementById("title-status-text");

    // If user is typing in the input field, clear the select dropdown
    if (titleInput.value.trim() !== "") {
      titleSelect.value = "";
      titleInput.disabled = false;
      titleInput.classList.remove("disabled");

      // Update status text
      if (statusText) {
        statusText.textContent = "Typing new title...";
        statusText.className = "form-text validation-warning";
      }
    } else {
      // Clear status text when input is empty
      if (statusText) {
        statusText.textContent = "Select an existing title or type a new one";
        statusText.className = "form-text";
      }
    }
  }

  resetFormAfterSubmission() {
    // Reset the form
    document.getElementById("grower-product-form").reset();

    // Clear image preview
    document.getElementById("image-preview").innerHTML = "";

    // Reset title input state
    this.initializeTitleInputState();

    // Clear any validation errors
    this.clearValidationErrors();

    // Reset status text
    const statusText = document.getElementById("title-status-text");
    if (statusText) {
      statusText.textContent = "Select an existing title or type a new one";
      statusText.className = "form-text";
    }
  }

  async uploadImagesToShopify(files) {
    if (!files || files.length === 0) {
      console.log("No files provided for existing product upload");
      return [];
    }

    try {
      // Step 1: Prepare metadata for stagedUploadsCreate
      const imageMetadata = files.map((file) => ({
        filename: file.name,
        mimeType: file.type,
        resource: "FILE",
        fileSize: file.size.toString(),
        httpMethod: "POST",
      }));

      // Step 2: Call /upload-images to get staged upload URLs
      const uploadRes = await fetch(
        `https://growers-bk.vercel.app/api/shopify/upload-images`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ files: imageMetadata }),
        }
      );

      if (!uploadRes.ok) {
        throw new Error(`Upload images API failed: ${uploadRes.status}`);
      }

      const uploadData = await uploadRes.json();
      const stagedTargets =
        uploadData?.data?.stagedUploadsCreate?.stagedTargets;

      if (!stagedTargets || stagedTargets.length === 0) {
        throw new Error("No staged targets returned from upload API");
      }

      // Step 3: Upload each file to the corresponding staged URL
      await Promise.all(
        stagedTargets.map(async (target, index) => {
          const formData = new FormData();

          // Add all required parameters
          target.parameters.forEach((param) => {
            formData.append(param.name, param.value);
          });

          // Add the actual file
          formData.append("file", files[index]);

          // Upload to Shopify S3
          const s3Res = await fetch(target.url, {
            method: "POST",
            body: formData,
          });

          if (!s3Res.ok) {
            throw new Error(
              `Failed to upload file to Shopify S3: ${s3Res.status}`
            );
          }
        })
      );

      // Step 4: Register uploaded files with Shopify using fileCreate
      const fileInputs = stagedTargets.map((item, index) => ({
        alt: "product image-" + index,
        originalSource: item.resourceUrl,
        contentType: "IMAGE",
      }));

      const createRes = await fetch(
        `https://growers-bk.vercel.app/api/shopify/create-url`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ files: fileInputs }),
        }
      );

      if (!createRes.ok) {
        throw new Error(`Create URL API failed: ${createRes.status}`);
      }

      const createData = await createRes.json();
      const fileIds = createData?.data?.fileCreate?.files?.map((f) => f.id);

      if (!fileIds || fileIds.length === 0) {
        throw new Error("No file IDs returned from create URL API");
      }

      // Optional: Wait for Shopify to generate previews
      await new Promise((resolve) => setTimeout(resolve, 3000));

      // Step 5: Fetch preview images using file IDs
      const previewRes = await fetch(
        `https://growers-bk.vercel.app/api/shopify/preview-image`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ ids: fileIds }),
        }
      );

      if (!previewRes.ok) {
        throw new Error(`Preview image API failed: ${previewRes.status}`);
      }
      const previewData = await previewRes.json();
      return previewData;
    } catch (error) {
      console.error("Error uploading images to Shopify:", error);
      throw error;
    }
  }

  setupVarietyValidationListeners(varietyRow) {
    // Add validation clear listeners to all input fields in the variety row
    const inputs = varietyRow.querySelectorAll("input, select");
    inputs.forEach((input) => {
      input.addEventListener("input", () => {
        this.clearFieldError(input);
      });
      input.addEventListener("change", () => {
        this.clearFieldError(input);
      });
    });
  }

  async handleFormSubmit(e) {
    e.preventDefault();

    // Validate form before showing spinner
    if (!this.validateForm()) {
      return;
    }

    // Show spinner immediately when form is submitted
    this.showSpinner("Saving product...");

    const titleInput = document.getElementById("product-title-input");
    const titleSelect = document.getElementById("product-title-select");
    const title = titleInput.value || titleSelect.value;
    const quantity = parseInt(document.getElementById("quantity").value, 10);
    const fileInput = document.getElementById("product-images");
    const files = Array.from(fileInput.files); // Convert to array to preserve files

    try {
      // Get grower token
      const growerToken = this.getGrowerToken();
      const growerId = growerToken.grower.id;

      // Generate SKU based on first variety or product title
      let sku;
      const titleSku = title.replace(/\s+/g, "-");
      sku = this.generateSku(growerId, titleSku);

      // Check if product already exists in both Backend and Shopify
      this.showSpinner("Checking existing products...");

      // Check Shopify products
      const shopifyProducts = await this.getAllShopifyProducts();
      const existingShopifyProduct = shopifyProducts.find(
        (product) =>
          product.title &&
          product.title.toLowerCase().trim() === title.toLowerCase().trim()
      );

      let shopifyUpdated = false;
      let createdProductId = null;

      // Handle backend product using create-or-update API
      this.showSpinner("Creating/updating product...");
      const productPayload = {
        growerId: growerId,
        title: title,
        quantity: quantity,
        sku: sku,
        buyingPrice: 0,
        images: [], // Start with empty images array
      };

      let apiResult;
      if (this.isEditMode) {
        // Use regular update API for edit mode
        apiResult = await this.createOrUpdateProduct(
          productPayload,
          "PUT",
          `https://growers-bk.vercel.app/api/products/${this.productId}`,
          growerToken.token
        );
        createdProductId = apiResult.product._id || this.productId;
        this.createToast("success", `Product updated successfully!`);
      } else {
        // Create new product
        apiResult = await this.createOrUpdateProduct(
          productPayload,
          "POST",
          "https://growers-bk.vercel.app/api/products",
          growerToken.token
        );
        createdProductId = apiResult.product._id;
        this.createToast("success", `Product created successfully!`);
      }
      if (createdProductId && files && files.length > 0) {
        try {
          this.showSpinner("Uploading images...");
          const uploadedImages = await this.uploadImagesToShopify(
            files,
          );
          this.showSpinner("Updating product with images...");
          const updatePayload = {
            growerId: growerId,
            title: title,
            quantity: quantity,
            sku: sku,
            buyingPrice: 0,
            images: uploadedImages?.data?.nodes?.map(
              (image) => image.image.originalSrc || image
            ),
          };

          await this.createOrUpdateProduct(
            updatePayload,
            "PUT",
            `https://growers-bk.vercel.app/api/products/${createdProductId}`,
            growerToken.token
          );

          this.createToast(
            "success",
            `Product images uploaded successfully! (${files.length} image(s))`
          );
        } catch (imageError) {
          this.createToast(
            "error",
            `Product saved but image upload failed: ${imageError.message}`
          );
        }
      }
      if (shopifyUpdated) {
        this.createToast("success", `Product quantities updated in Shopify`);
      }

      this.hideSpinner();

      // Reset form AFTER all operations are complete
      setTimeout(() => {
        this.resetFormAfterSubmission();
      }, 1000);

      // Redirect back to dashboard after successful operation
      // setTimeout(() => {
      //   window.location.href = '/pages/grower-account-page';
      // }, 2000);
    } catch (err) {
      this.hideSpinner();
      this.createToast("error", "Upload failed: " + err.message);
    }
  }

  async testInventoryLevelAPI() {
    try {
      console.log("Calling inventory level API...");

      const response = await fetch(
        "https://growers-bk.vercel.app/api/shopify/inventory-level",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `API call failed: ${response.status} ${response.statusText}`
        );
      }

      const data = await response.json();
      console.log("Inventory Level API Response:", data);

      // Show success toast
      this.createToast(
        "success",
        "Inventory level data fetched successfully! Check console for details."
      );
    } catch (error) {
      console.error("Error calling inventory level API:", error);
      this.createToast(
        "error",
        `Failed to fetch inventory level: ${error.message}`
      );
    }
  }
}

// Initialize the product form
const productForm = new ProductForm();
